/* eslint-disable @typescript-eslint/no-explicit-any */
// Mock data structure for nested loss types
// Each level contains multiple options
// Level 1: First party loss vs Third party loss (radio buttons)
// Levels 2-5: Nested checkboxes that appear conditionally based on parent selection

export interface LossType {
  id: string;
  label: string;
  children?: LossType[];
}

export interface LossTypeCategory {
  id: string;
  label: string;
  options: LossType[];
}

// Function to generate consistent IDs from names
function generateId(name: string, prefix = ''): string {
  const cleanName = name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, '-'); // Replace spaces with hyphens
  return prefix ? `${prefix}-${cleanName}` : cleanName;
}

// Function to transform lossTypes JSON data to component format
export function transformApiDataToLossTypes(
  lossTypes: unknown
): LossTypeCategory[] {
  // Handle case where lossTypes might be a string (JSON string) or already parsed object
  let parsedLossTypes: unknown;

  if (typeof lossTypes === 'string') {
    try {
      parsedLossTypes = JSON.parse(lossTypes);
    } catch (err) {
      console.error('Failed to parse lossTypes JSON string:', err);
      return [];
    }
  } else {
    parsedLossTypes = lossTypes;
  }

  // If lossTypes is empty or not an object, return empty array
  if (!parsedLossTypes || typeof parsedLossTypes !== 'object') {
    return [];
  }

  // If it's already an array in the correct format (has id, label, options structure)
  if (Array.isArray(parsedLossTypes)) {
    // Check if it's already in LossTypeCategory format
    const firstItem = parsedLossTypes[0];
    if (
      firstItem &&
      typeof firstItem === 'object' &&
      'id' in firstItem &&
      'label' in firstItem &&
      'options' in firstItem
    ) {
      // It's already in the correct LossTypeCategory format, return as-is
      return parsedLossTypes as LossTypeCategory[];
    }

    // Handle other array formats (like potentialCoveragesImpacted)
    const lossTypeCategories: LossTypeCategory[] = [];
    parsedLossTypes.forEach((group) => {
      if (
        group &&
        typeof group === 'object' &&
        'type' in group &&
        'coverages' in group
      ) {
        const typeLabels: Record<string, string> = {
          first_party: 'First Party',
          first_party_loss: 'First Party Loss',
          liability: 'Third Party Liability',
        };

        const groupType =
          typeof group.type === 'string' ? group.type : 'unknown';
        const coverages = Array.isArray(group.coverages) ? group.coverages : [];

        const category: LossTypeCategory = {
          id: generateId(groupType),
          label: typeLabels[groupType] || groupType,
          options: coverages.map((coverage: any) => {
            const coverageName =
              coverage &&
              typeof coverage === 'object' &&
              'name' in coverage &&
              typeof coverage.name === 'string'
                ? coverage.name
                : 'Unknown Coverage';

            const coverageOption: LossType = {
              id: generateId(coverageName, generateId(groupType)),
              label: coverageName,
            };

            if (
              coverage &&
              typeof coverage === 'object' &&
              'sub_coverages' in coverage &&
              Array.isArray(coverage.sub_coverages) &&
              coverage.sub_coverages.length > 0
            ) {
              coverageOption.children = coverage.sub_coverages.map(
                (subCoverage: any) => {
                  const subCoverageName =
                    subCoverage &&
                    typeof subCoverage === 'object' &&
                    'name' in subCoverage &&
                    typeof subCoverage.name === 'string'
                      ? subCoverage.name
                      : 'Unknown Sub-coverage';
                  return {
                    id: generateId(subCoverageName, coverageOption.id),
                    label: subCoverageName,
                  };
                }
              );
            }

            return coverageOption;
          }),
        };

        lossTypeCategories.push(category);
      }
    });
    return lossTypeCategories;
  }
  // Handle other possible structures of lossTypes JSON
  else if (typeof parsedLossTypes === 'object' && parsedLossTypes !== null) {
    // If it's a flat object with loss type selections, create categories from it
    const lossTypeCategories: LossTypeCategory[] = [];
    Object.keys(parsedLossTypes).forEach((key) => {
      const value = (parsedLossTypes as Record<string, unknown>)[key];
      if (value) {
        const category: LossTypeCategory = {
          id: generateId(key),
          label: key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (l) => l.toUpperCase()),
          options: Array.isArray(value)
            ? value.map((item, index) => ({
                id: generateId(`${key}-${index}`),
                label:
                  typeof item === 'string'
                    ? item
                    : item &&
                      typeof item === 'object' &&
                      'name' in item &&
                      typeof item.name === 'string'
                    ? item.name
                    : item &&
                      typeof item === 'object' &&
                      'label' in item &&
                      typeof item.label === 'string'
                    ? item.label
                    : `Item ${index + 1}`,
              }))
            : [
                {
                  id: generateId(`${key}-single`),
                  label: typeof value === 'string' ? value : 'Selected',
                },
              ],
        };

        lossTypeCategories.push(category);
      }
    });
    return lossTypeCategories;
  }

  return [];
}
